import * as React from 'react';

import { Button } from '../components/buttons';
import {
	BirthDatePicker,
	CountryDropdown,
	DropdownInput,
	Label,
	ProvinceDropdown,
	TextInput,
	ToggleSwitch
} from '../components/forms';
import { TooltipModal } from '../components/common';
import Utils, { InputError, Validation, BeneficiaryTypes } from '../common/utils';

interface Props {
	className?: string;
	customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto;
	policies: CustomerPolicy[];
	match: any;
	onPolicyBeneficiaryChange: (e: any) => void;
	onRelationshipChange: (e: any) => void;
	save: () => void;
	saveAndAdd: () => void;
	cancel: (e: any) => void;
}
interface State {
	touched: {
		birthDate: boolean;
		email: boolean;
		postalCode: boolean;
	};
	relationshipOther: string;
}
class PolicyBeneficiaryForm extends React.Component<Props, State> {
	errors = { birthDate: {} as InputError, email: {} as InputError, postalCode: {} as InputError };
	state = {
		touched: {
			birthDate: false,
			email: false,
			postalCode: false,
		},
		relationshipOther: ''
	};

	onChange = (e: any) => {
		let value = Utils.parseValueFromEvent(e);
		this.setState({ [e.target.name]: value });
	}

	handleBlur = (field: any) => (evt: any) => {
		this.setState({
			touched: { ...this.state.touched, [field]: true }
		});
		if (field === 'birthDate') {
			const underageChangeEvent = {
				target: {
					name: 'isUnderage',
					value: Utils.getAgeFromBirthdate(this.props.customerPolicyBeneficiary.birthDate) < 18,
					type: 'custom'
				}
			};
			this.props.onPolicyBeneficiaryChange(underageChangeEvent);
		}
	}

	validate = () => {
		// true means invalid
		return {
			birthDate: Validation.validateDate(this.props.customerPolicyBeneficiary.birthDate),
			email: Validation.validateEmail(this.props.customerPolicyBeneficiary.email),
			postalCode: Validation.validatePostalCode(this.props.customerPolicyBeneficiary.postalCode)
		};
	}

	isSubmitDisabled = () => {
		if (this.errors.birthDate.hasError || this.errors.email.hasError || this.errors.postalCode.hasError) {
			return true;
		}

		const { firstName, lastName, birthDate, customerPolicyId } = this.props.customerPolicyBeneficiary;
		return !firstName || !lastName || !birthDate || !customerPolicyId;
	}

	render() {
		this.errors = this.validate();
		return (
			<div className={this.props.className}>
				<h2 className="form-fieldset-title">
					Beneficiary info
				</h2>
				<div className="form-fieldset">
					<div className="row">
						<TextInput
							id="beneficiaryFirstName"
							labelText="First Name"
							name="firstName"
							onChange={this.props.onPolicyBeneficiaryChange}
							value={this.props.customerPolicyBeneficiary.firstName}
							className="col-md-4"
							required={true}
						/>
						<TextInput
							id="beneficiaryLastName"
							labelText="Last Name"
							name="lastName"
							onChange={this.props.onPolicyBeneficiaryChange}
							value={this.props.customerPolicyBeneficiary.lastName}
							className="col-md-4"
							required={true}
						/>
						<div className="form-daypicker-responsive col-md-4">
							<BirthDatePicker
								id="beneficiaryDOB"
								labelText="Birthdate"
								name="birthDate"
								value={this.props.customerPolicyBeneficiary.birthDate}
								onBlur={this.handleBlur('birthDate')}
								onChange={this.props.onPolicyBeneficiaryChange}
								error={
									Utils.shouldMarkError(this.errors.birthDate, this.state.touched.birthDate) ? this.errors.birthDate : undefined
								}
								required={true}
							/>
						</div>
						<TextInput
							id="beneficiaryEmail"
							labelText="Beneficiary Email"
							name="email"
							onBlur={this.handleBlur('email')}
							onChange={this.props.onPolicyBeneficiaryChange}
							value={this.props.customerPolicyBeneficiary.email}
							error={
								Utils.shouldMarkError(this.errors.email, this.state.touched.email) ? this.errors.email : undefined
							}
							className="col-md-6"
						/>
						<ToggleSwitch
							labelText="Send notification"
							name="shouldSendNotification"
							onChildren="Yes"
							offChildren="No"
							onChange={this.props.onPolicyBeneficiaryChange}
							checked={this.props.customerPolicyBeneficiary.shouldSendNotification}
							className="col-md-4 toggle-yes-no"
							disabled={Utils.isEmpty(this.props.customerPolicyBeneficiary.email)}
						/>
						<DropdownInput
							labelText="Relationship"
							id="relationship"
							name="relationship"
							value={this.props.customerPolicyBeneficiary.relationship}
							defaultOption="-"
							onChange={this.props.onRelationshipChange}
							options={[
								{
									text: 'Spouse',
									value: 'Spouse'
								},
								{
									text: 'Child',
									value: 'Child'
								},
								{
									text: 'Grandchild',
									value: 'Grandchild'
								},
								{
									text: 'Parent',
									value: 'Parent'
								},
								{
									text: 'Sibling',
									value: 'Sibling'
								},
								{
									text: 'Other - specify',
									value: 'Other'
								}
							]}
							className="col-md-3"
						/>
						{this.props.customerPolicyBeneficiary.relationship === 'Other' &&
							<TextInput
								id="relationshipOther"
								labelText=" "
								name="relationshipOther"
								onChange={this.props.onPolicyBeneficiaryChange}
								value={this.props.customerPolicyBeneficiary.relationshipOther}
								maxLength={20}
								className="col-md-2"
							/>
						}
						<div className="col-md-auto form-group">
							<div className="mb-1">
								<Label className="mr-2 mb-0 strong">Revocability</Label>
								<TooltipModal toggle="What does this mean?" toggleClassName="em btn-link-inline">
									<p>
										If you designate a beneficiary as irrevocable, you cannot change ("revoke")
										the claim benefits of that beneficiary without their signature. If the irrevocable beneficiary is a minor, you
										cannot  change or revoke the beneficiary until minor reaches the age of majority.
									</p>
									<p>
										Note: In Quebec, the designation of a spouse is irrevocable unless you check the revocable box
										next to their name.
									</p>
								</TooltipModal>
							</div>
							<ToggleSwitch
								name="isRevocable"
								onChildren="Revocable"
								offChildren="Irrevocable"
								onChange={this.props.onPolicyBeneficiaryChange}
								checked={this.props.customerPolicyBeneficiary.isRevocable}
								className="mr-2 mb-0 toggle-buttons-wrap"
							/>
						</div>
						<div className="col-md-auto form-group">
							<div className="mb-1">
								<Label className="mr-2 mb-0 strong">Primary/Contingent</Label>
								<TooltipModal toggle="What does this mean?" toggleClassName="em btn-link-inline">
									<p>
										The primary beneficiary receives the benefits payable when the insured dies.
										If more than one primary beneficiary is appointed, proceeds will be payable as per the percentages you 
										indicated. Note that the sum of percentages assigned to each beneficiary must total to 100%.
									</p>
									<p>
										If multiple primary beneficiaries are designated, but no percentage allocation has been indicated, benefits
										will be paid out in equal shares. If no beneficiaries have been indicated, benefits from the insurance policy
										will be paid to the insured's estate.
									</p>
									<p>
										In the event that all primary beneficiaries die before the insured, the
										contingent beneficiaries will receive the benefits payable when the insured dies.
									</p>
								</TooltipModal>
							</div>
							<ToggleSwitch
								name="typeId"
								onChildren="Primary"
								offChildren="Contingent"
								onChange={this.props.onPolicyBeneficiaryChange}
								checked={this.props.customerPolicyBeneficiary.typeId === BeneficiaryTypes.Primary}
								className="mr-2 mb-0 toggle-buttons-wrap"
							/>
						</div>
					</div>
				</div>
				<h2 className="form-fieldset-title mt-4">Contact info</h2>
				<div className="form-fieldset">
					<div className="row">
						<TextInput
							id="beneficiaryAddress"
							labelText="Address"
							name="address"
							onChange={this.props.onPolicyBeneficiaryChange}
							value={this.props.customerPolicyBeneficiary.address}
							className="col-md-12"
						/>
						<TextInput
							id="beneficiaryCity"
							labelText="City"
							name="city"
							onChange={this.props.onPolicyBeneficiaryChange}
							value={this.props.customerPolicyBeneficiary.city}
							className="col-md-6"
						/>
						<ProvinceDropdown
							id="beneficiaryProvince"
							labelText="Province"
							name="province"
							onChange={this.props.onPolicyBeneficiaryChange}
							province={this.props.customerPolicyBeneficiary.province}
							className="col-sm-6 mb-sm-2"
						/>
						<TextInput
							labelText="Postal code"
							name="postalCode"
							onBlur={this.handleBlur('postalCode')}
							onChange={this.props.onPolicyBeneficiaryChange}
							value={this.props.customerPolicyBeneficiary.postalCode}
							className="col-sm-6"
							error={
								Utils.shouldMarkError(this.errors.postalCode, this.state.touched.postalCode)
									? this.errors.postalCode
									: undefined
							}
						/>
						<CountryDropdown
							id="beneficiaryCountry"
							labelText="Country"
							name="country"
							onChange={this.props.onPolicyBeneficiaryChange}
							country={this.props.customerPolicyBeneficiary.country}
							className="col-sm-6 mb-2"
						/>
					</div>
				</div>
				<h2 className="form-fieldset-title mt-4">Policy</h2>
				<div className="form-fieldset">
					<div className="row">
						<DropdownInput
							id="beneficiaryPolicyNumber"
							labelText="Policy #"
							name="customerPolicyId"
							value={this.props.customerPolicyBeneficiary.customerPolicyId}
							defaultOption="-"
							onChange={this.props.onPolicyBeneficiaryChange}
							options={this.props.policies.map(policy => {
								return { text: policy.number!, value: policy.id! };
							})}
							className="col-md-6 mb-2"
							required={true}
						/>
					</div>
				</div>
				<div className="mt-3">
					<Button
						id={this.props.customerPolicyBeneficiary.id ? 'updateBeneficiaryBtn' : `addBeneficiaryBtn`}
						onClick={this.props.save}
						className="btn-primary mt-3 mr-3"
						disabled={this.isSubmitDisabled()}
					>
						{this.props.customerPolicyBeneficiary.id ? 'Update beneficiary' : 'Add beneficiary'}
					</Button>
					<br className="d-sm-none" />
					{!(this.props.customerPolicyBeneficiary.id) &&
						<span>
							<Button
								id="saveAndAddAnotherBtn"
								onClick={this.props.saveAndAdd}
								className="btn-primary mr-3 mt-3"
								disabled={this.isSubmitDisabled()}
							>
								Save and Add Another
							</Button>
							<br className="d-sm-none" />
						</span>
					}
					<Button
						onClick={this.props.cancel}
						className="btn-outline-primary mr-3 mt-3"
					>
						Cancel
					</Button>
				</div>
			</div>
		);
	}
}
export default PolicyBeneficiaryForm;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/policy/PolicyBeneficiaryForm.tsx